import { Locales, route } from "@/lib/navigationFunctions";

import { getTranslations } from "next-intl/server";
import { MarketingTransformationPage, MarketingTransformationPath, MarketingValues } from "@/lib/types/shared/page.types";

export const marketingTransformationPaths: MarketingTransformationPath[] = [
    {
        id: 'seo',
        name: 'seo_optimalization',
        link: {
            en: '/search-engine-optimization',
            nl: '/zoekmachine-optimalisatie'
        },
    },
    {
        id: 'sea',
        name: 'sea_advertising',
        link: {
            en: '/advertising-in-google',
            nl: '/adverteren-in-google'
        },
    },
    {
        id: 'cro',
        name: 'cro_optimization',
        link: {
            en: '/conversion-optimization',
            nl: '/conversie-optimalisatie'
        },
    },
    {
        id: 'texts',
        name: 'texts_and_content',
        link: {
            en: '/marketing-transformation/texts',
            nl: '/marketing-transformatie/teksten'
        },
    },
    {
        id: 'branding-strategy',
        name: 'branding_strategy',
        link: {
            en: '/branding-strategy',
            nl: '/branding-strategie'
        },
    },
    {
        id: 'social-media',
        name: 'social_media',
        link: {
            en: '/social-media',
            nl: '/social-media'
        },
    },
];

const getMarketingTransformationConstants = async (locale: Locales | 'nl') => {
    let t = (variable: string) => {
        return variable;
    }

    if (locale) {
        t = await getTranslations({ locale: locale });
    }

    const marketingTransformationPages: MarketingTransformationPage[] = [
        {
            id: 'texts',
            title: t('writing_your_texts'),
            description: t('strengthen_your_marketing_strategy_with_the_optimized_textual_content_of_webbeukers_improve_your_brand_communication_and_engage_your_target_group'),
            // keywords: `${t('texts_and_content')}, ${t('content_creation')}, ${t('webbeukers')}, ${t('marketing_transformation')}, ${t('target_group_engagement')}`,
            pageLayout: [
                {
                    type: "component-section",
                    subType: "hero-nd",
                    title: t('pages.marketing_transformation.texts.hero.title'),
                    // text: t('pages.marketing_transformation.texts.hero.text'),
                    // certificatesText: t('pages.marketing_transformation.hero.certificates'),
                    // disableBeuker: true,
                    buttons: [
                        {
                            variant: "green",
                            text: t("pages.marketing_transformation.hero.buttons.0"),
                            link: route("/contact", locale),
                        },
                        {
                            text: t("pages.marketing_transformation.hero.buttons.1"),
                            link: route("/contact", locale),
                            variant: "orange-300",
                        },
                    ]
                },
                {
                    type: "component-section",
                    componentKey: "NormalSection",
                    componentProps: {
                        title: t("professional_content_creation_your_key_to_effective_online_communication"),
                        text: t("at_webbeukers_we_understand_the_power_of_words_writing_effective_texts_is_an_art_and_a_science_at_the_same_time_whether_its_web_content_blog_posts_ad_copy_or_technical_documentation_our_team_of_experienced_writers_dive_deep_into_your_brand_story_to_create_messages_that_resonate_with_a_perfect_mix_of_creativity_and_strategic_insight_we_ensure_that_every_text_not_only_appeals_to_your_target_group_but_also_contributes_to_your_business_objectives"),
                        className: "bg-white",
                        theme: "dark"
                    }
                },
                {
                    type: "component",
                    componentKey: "ImageBlock",
                    componentProps: {
                        title: t("turn_words_into_profit_effective_content_by_professionals"),
                        ul: [
                            {
                                title: t("improved_quality_and_professionalism"),
                                text: t("professionally_written_texts_ensure_a_higher_quality_standard_they_are_grammatically_correct_well_structured_and_reflect_a_professional_image_which_is_essential_for_building_trust_with_your_target_audience"),
                                icon: "Icons/plus_kefs9b",
                                alt: t("plus_icon")
                            },
                            {
                                title: t("time_and_cost_savings"),
                                text: t("outsourcing_copywriting_saves_you_time_and_resources_this_allows_you_to_focus_on_other_crucial_aspects_of_your_business_while_experts_take_care_of_your_content_needs"),
                                icon: "Icons/plus_kefs9b",
                                alt: t("plus_icon")
                            },
                            {
                                title: t("consistent_and_compelling_content"),
                                text: t("by_writing_copy_you_ensure_a_consistent_tone_and_style_that_matches_your_brand_identity_while_keeping_the_content_engaging_and_relevant_to_your_readers_this_increases_the_chance_of_returning_visitors_and_loyal_customers"),
                                icon: "Icons/plus_kefs9b",
                                alt: t("plus_icon")
                            },
                        ],
                        image: "Articles/texts_e904sh",
                        imagePosition: "left",
                        alt: t("an_image_depicting_the_process_of_writing_texts_with_webbeukers_possibly_featuring_writing_tools_or_a_person_typing"),
                        cols: {
                            left: 'w-full md:w-1/2',
                            right: 'w-full md:w-1/2'
                        },
                        imageWidth: "w-full",
                        theme: "dark"
                    }
                },
                {
                    type: "component-section",
                    componentKey: "ContactSectionNew",
                    componentProps: {
                        title: null,
                        text: null,
                    }
                },
                {
                    type: "component-section",
                    componentKey: "NormalSection",
                    componentProps: {
                        title: t("our_content_creation_services"),
                        children: {
                            type: "component",
                            componentKey: "ArrowCardGrid",
                            componentProps: {
                                cards: [
                                    {
                                        title: t("web_content_writing"),
                                        text: t("creating_engaging_and_informative_content_for_websites_including_homepage_copy_about_us_pages_services_pages_and_more"),
                                        theme: "transparent",
                                        textColor: "text-primary-gray-950",
                                    },
                                    {
                                        title: t("blog_post_and_article_writing"),
                                        text: t("this_includes_setting_up_managing_and_continuously_optimizing_your_advertising_campaigns_to_improve_performance_and_maximize_your_roi"),
                                        theme: "transparent",
                                        textColor: "text-primary-gray-950",
                                    },
                                    {
                                        title: t("seo_copywriting"),
                                        text: t("writing_optimized_content_for_search_engines_with_the_correct_use_of_keywords_to_improve_your_online_visibility_and_search_engine_ranking"),
                                        theme: "transparent",
                                        textColor: "text-primary-gray-950",
                                    },
                                    {
                                        title: t("advertising_and_marketing_copywriting"),
                                        text: t("creating_catchy_and_persuasive_copy_for_advertising_and_marketing_campaigns_including_slogans_email_campaigns_and_social_media_content"),
                                        theme: "transparent",
                                        textColor: "text-primary-gray-950",
                                    }
                                ]
                            }
                        },
                        theme: "dark",
                        className: "bg-white"
                    }
                },
                {
                    type: "component-section",
                    componentKey: "ProcessSection",
                    componentProps: {
                        addReviews: true,
                        reviewsOrder: "before"
                    }
                },
            ]
        },
    ];

    for (const page of marketingTransformationPages) {
        const path = marketingTransformationPaths.find(path => path.id === page.id);

        if (path) {
            page.link = path.link;
        }
    }

    const marketingCorevalues: MarketingValues[] = [
        {
            id: "seo",
            title: "seo_search_engine_optimization",
            text: "increase_your_online_findability_with_our_tailormade_seo_strategies_we_ensure_that_your_website_is_not_only_found_but_also_excels_in_the_search_results",
        },
        {
            id: "sea",
            title: "sea_search_engine_advertising",
            text: "reach_your_target_group_quickly_and_effectively_with_our_sea_solutions_we_manage_and_optimize_your_paid_search_campaigns_for_maximum_impact",
        },
        {
            id: "cro",
            title: "cro_conversion_optimization",
            text: "increase_the_effectiveness_of_your_website_our_cro_strategies_are_designed_to_convert_visitors_into_customers",
        },
        {
            id: "texts",
            title: "have_texts_written",
            text: "have_your_brand_story_told_by_our_professional_copywriters_we_create_compelling_content_that_fits_your_brand_and_attracts_your_target_group",
        },
        {
            id: "branding-strategy",
            title: "branding_and_strategy",
            text: "develop_an_unforgettable_brand_identity_with_our_creative_branding_and_strategic_planning_we_help_you_stand_out_in_a_crowded_market",
        },
        {
            id: "social-media",
            title: "social_media_management",
            text: "strengthen_your_social_media_presence_our_social_media_strategies_increase_your_visibility_and_engagement_on_platforms_that_matter_to_you",
        },
    ];

    for (const coreValue of marketingCorevalues) {
        const path = marketingTransformationPaths.find(path => path.id === coreValue.id);

        if (path) {
            coreValue.link = path.link;
        }
    }


    return {
        marketingTransformationPages,
        marketingCorevalues,
    };
}

export default getMarketingTransformationConstants;